import React from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import IconAssistant from "@/assets/assistant.svg?react";
import { DownloadIcon } from "@/components/atomic/DownloadIcon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/atomic/Tooltip";
import { TypingIndicator } from "@/components/atomic/TypingIndicator";
import { useDownloadMessageVocs } from "@/hooks/useDownloadMessageVocs";
import { cn } from "@/lib/utils";
import { Message } from "@/models/conversation";
import { useChatStore } from "@/stores/ui/useChatStore";

interface AssistantMessageProps {
  message?: Message;
  isLoading?: boolean;
  onViewVocs?: () => void;
  messageRefs?: React.MutableRefObject<Record<string, HTMLDivElement>>;
}

export const AssistantMessage: React.FC<AssistantMessageProps> = ({
  message,
  isLoading,
  onViewVocs,
  messageRefs,
}) => {
  const { t } = useTranslation("voiceek_ui");

  const hasVocIds = Boolean(message?.vocIds?.length);
  const conversationId = useChatStore((state) => state.currentConversationId);

  const { handleDownload, isDownloading } = useDownloadMessageVocs(
    conversationId,
    message?.id || null,
  );

  return (
    <div className="flex gap-4">
      <div className="w-6 h-6 flex-shrink-0 mt-0.5 rounded-full overflow-hidden">
        <IconAssistant />
      </div>

      <div
        className={cn(
          "bg-white p-5 rounded-md box-border flex flex-col gap-4",
          "border-1 border-white transition-all duration-200", // 関連VoC表示時のハイライト後の元の状態
        )}
        ref={(el) => {
          if (el && message && messageRefs) messageRefs.current[message.id] = el;
        }}
        data-testid={message ? `assistant-message-${message.id}` : ""}
      >
        {isLoading && <TypingIndicator />}
        {!isLoading && message && (
          <>
            <div className="text-[#474747] leading-relaxed break-all prose prose-sm markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                {message.content}
              </ReactMarkdown>
            </div>
            {hasVocIds && (
              <div className="flex justify-end">
                <button
                  onClick={onViewVocs}
                  className="text-[#6888E1] underline cursor-pointer"
                >
                  {t("chat.modal.viewVocs", { count: message?.vocIds?.length || 0 })}
                </button>
              </div>
            )}
            {hasVocIds && (
              <div className="flex">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        disabled={isDownloading}
                        onClick={handleDownload}
                        className={cn(
                          "w-[26px] h-[26px] items-center flex text-[#6888E1]",
                          "hover:text-[#3C569D] hover:cursor-pointer",
                        )}
                        data-testid="download-message-vocs-button"
                      >
                        <DownloadIcon isLoading={isDownloading} className="w-[17px] h-[17px]" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="bg-[#293d72] text-white text-[10px] font-semibold px-2 py-1 rounded-[5px]"
                    >
                      {t("chat.download.tooltip")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
